import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Select } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  ArrowLeftIcon,
  NewEngagementIcon,
  SearchIcon,
  CheckmarkIcon,
  TileVenueIcon,
  TileMedicalIcon,
  TileVolunteerIcon,
  TilePhotoIcon,
  StatusInProgressIcon,
  StatusPendingIcon,
  StatusAssignedIcon,
  PaperclipIcon,
  DownloadIcon,
  EditSquareIcon,
  TrashIcon,
} from '../../assets/icons';
import CommentsPanel from '../../components/CommentsPanel';
import ActivityHistoryPanel from '../../components/ActivityHistoryPanel';
import { useGetEngagementQuery, engName } from '../../features/engagements/engagementsApi';
import {
  useGetTaskQuery,
  useGetSubtasksQuery,
  useDeleteTaskMutation,
  taskTitle,
  taskDesc,
  taskLocation,
  taskTags,
  taskDate,
  taskRawStatus,
  taskAssigneeName,
  statusKey,
  API_STATUS,
  type Task,
  type UiTaskStatus,
} from '../../features/tasks/tasksApi';

type IconType = typeof TileVenueIcon;
type SubStatus = 'completed' | 'inprogress' | 'pending' | 'assigned';

/** The subtask table renders four states; scheduled/planning collapse to pending. */
const toSubStatus = (s: UiTaskStatus): SubStatus =>
  s === 'scheduled' || s === 'planning' ? 'pending' : s;

const TILES: { Tile: IconType; tileBg: string; tileColor: string }[] = [
  { Tile: TileVenueIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
  { Tile: TileMedicalIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
  { Tile: TileVolunteerIcon, tileBg: 'bg-[#f3f4f6]', tileColor: 'text-[#6b7280]' },
  { Tile: TilePhotoIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#60a5fa]' },
];

const GRID = 'grid grid-cols-[minmax(0,1fr)_160px_140px_160px_120px_90px] items-center';
const TH = 'text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]';

/** Compact tag chips — shows the first two, then a "+N" overflow chip. */
function TagChips({ tags }: { tags: string[] }) {
  if (tags.length === 0) return <span className="text-xs text-[#9ca3af]">—</span>;
  const shown = tags.slice(0, 2);
  const extra = tags.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <span
          key={t}
          className="inline-flex max-w-[84px] items-center truncate rounded-full bg-brand-shade px-2.5 py-1 text-xs font-medium text-brand"
        >
          {t}
        </span>
      ))}
      {extra > 0 && <span className="text-xs font-medium text-ink-secondary">+{extra}</span>}
    </div>
  );
}

/** Two-letter initials for an avatar (the API carries no photo). */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function StatusCell({ status }: { status: SubStatus }) {
  if (status === 'completed')
    return (
      <span className="flex items-center gap-2 text-sm font-medium text-[#2563eb]">
        <span className="flex size-4 items-center justify-center rounded-full bg-[#dbeafe]">
          <CheckmarkIcon className="size-3" />
        </span>
        Completed
      </span>
    );
  if (status === 'inprogress')
    return (
      <span className="flex items-center gap-2 text-sm font-medium text-[#0f52ba]">
        <StatusInProgressIcon className="size-4" />
        In Progress
      </span>
    );
  if (status === 'pending')
    return (
      <span className="flex items-center gap-2 text-sm font-medium text-[#9ca3af]">
        <StatusPendingIcon className="size-4" />
        Pending
      </span>
    );
  return (
    <span className="flex items-center gap-2 text-sm font-medium text-[#4b5563]">
      <StatusAssignedIcon className="size-4" />
      Assigned
    </span>
  );
}

export default function TaskDetailPage() {
  const navigate = useNavigate();
  const { id, taskId } = useParams();

  const { data: engagement } = useGetEngagementQuery(id!, { skip: !id });
  const { data: task } = useGetTaskQuery(taskId!, { skip: !taskId });

  const engagementName = engagement ? engName(engagement) : 'Engagement';
  const taskName = task ? taskTitle(task) : 'Task details';
  const assignee = task ? taskAssigneeName(task) : '';
  const detailPath = `/user/engagements/${id ?? ''}`;

  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const [subToDelete, setSubToDelete] = useState<Task | null>(null);

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  // Filter values are UI keys/lowercase; the server expects title-case labels.
  const statusParam = status ? API_STATUS[status as UiTaskStatus] : undefined;

  const { data, isFetching } = useGetSubtasksQuery(
    {
      parentTask: taskId!,
      ...(search ? { search } : {}),
      ...(statusParam ? { status: statusParam } : {}),
      page,
      pageSize,
    },
    { skip: !taskId },
  );

  const pageRows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;

  const openSub = (t: Task) =>
    navigate(`/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}/subtasks/${t.id}`, {
      state: { name: engagementName, taskName },
    });
  const editSub = (t: Task) =>
    navigate(`/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}/subtasks/${t.id}/edit`, {
      state: { name: engagementName, taskName },
    });
  const confirmDelete = async () => {
    if (!subToDelete) return;
    await deleteTask(subToDelete.id).unwrap();
    setSubToDelete(null);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs
        items={[
          { label: 'Engagements', to: '/user/engagements' },
          { label: engagementName, to: detailPath },
          { label: 'Task details' },
        ]}
      />

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(detailPath, { state: { name: engagementName } })}
            className="text-ink transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-7" />
          </button>
          <h1 className="text-2xl font-semibold text-ink">{taskName}</h1>
        </div>
        <button
          type="button"
          onClick={() =>
            navigate(`/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}/subtasks/new`, {
              state: { name: engagementName, taskName },
            })
          }
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-6 py-2.5 text-base font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <NewEngagementIcon className="size-5" />
          New Sub Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 rounded-xl border border-line-soft bg-white p-5 shadow-card lg:flex-row lg:items-end">
        <div className="flex flex-1 flex-col gap-2">
          <span className={TH}>Search Tasks</span>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search sub tasks..."
              className="w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] py-2.5 pl-10 pr-4 text-sm text-ink placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 lg:w-[220px]">
          <span className={TH}>Status</span>
          <Select
            className="w-full"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'completed', label: 'Completed' },
              { value: 'inprogress', label: 'In Progress' },
              { value: 'pending', label: 'Pending' },
              { value: 'assigned', label: 'Assigned' },
            ]}
          />
        </div>
      </div>

      {/* Sub-tasks table */}
      <div className="overflow-hidden rounded-xl border border-line-soft bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className={[GRID, 'border-b border-[#f3f4f6] px-6 py-4'].join(' ')}>
              <span className={TH}>Task Name</span>
              <span className={TH}>Tags</span>
              <span className={TH}>Status</span>
              <span className={TH}>Assigned To</span>
              <span className={TH}>Deadline</span>
              <span className={[TH, 'text-right'].join(' ')}>Actions</span>
            </div>

            {isFetching && total === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">Loading sub-tasks…</div>
            ) : pageRows.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">No sub-tasks found.</div>
            ) : (
              pageRows.map((t, i) => {
                const tile = TILES[(start + i) % TILES.length];
                const who = taskAssigneeName(t);
                return (
                  <div
                    key={t.id}
                    onClick={() => openSub(t)}
                    className={[
                      GRID,
                      'cursor-pointer px-6 py-4 transition-colors hover:bg-[#f8fafc]',
                      i > 0 ? 'border-t border-[#f9fafb]' : '',
                    ].join(' ')}
                  >
                    <div className="flex items-center gap-4 pr-4">
                      <span className={['flex size-10 shrink-0 items-center justify-center rounded-lg', tile.tileBg, tile.tileColor].join(' ')}>
                        <tile.Tile className="size-5" />
                      </span>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-semibold text-brand transition-colors hover:text-brand-dark">
                          {taskTitle(t)}
                        </span>
                        <span className="truncate text-xs text-[#64748b]">{taskLocation(t)}</span>
                      </div>
                    </div>
                    <div className="pr-3">
                      <TagChips tags={taskTags(t)} />
                    </div>
                    <div>
                      <StatusCell status={toSubStatus(statusKey(taskRawStatus(t)))} />
                    </div>
                    <div>
                      {who ? (
                        <div className="flex items-center gap-2">
                          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-shade text-[10px] font-semibold text-brand">
                            {initials(who)}
                          </span>
                          <span className="truncate text-sm font-medium text-[#64748b]">{who}</span>
                        </div>
                      ) : (
                        <span className="text-sm italic text-[#9ca3af]">Unassigned</span>
                      )}
                    </div>
                    <span className="text-sm text-[#64748b]">{taskDate(t) || '—'}</span>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        aria-label="Edit sub task"
                        onClick={(e) => {
                          e.stopPropagation();
                          editSub(t);
                        }}
                        className="text-ink-secondary transition-colors hover:text-brand"
                      >
                        <EditSquareIcon className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete sub task"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSubToDelete(t);
                        }}
                        className="text-ink-secondary transition-colors hover:text-[#ef4444]"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer */}
            <Pagination
              rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
              rangeEnd={Math.min(page * pageSize, total)}
              total={total}
              noun="tasks"
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Documentation */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center gap-2">
              <PaperclipIcon className="size-5 text-[#0f172a]" />
              <h2 className="text-base font-semibold text-[#0f172a]">Documentation</h2>
            </div>
            {task?.attachments && task.attachments.length > 0 ? (
              <div className="mt-4 flex flex-col gap-3">
                {task.attachments.map((a, idx) => (
                  <a
                    key={a.id ?? idx}
                    href={a.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-lg bg-brand-shade p-4 transition-colors hover:bg-[#e2ecff]"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex size-10 items-center justify-center rounded bg-white text-xs font-bold uppercase text-[#ef4444] shadow-card">
                        {(a.file_type || 'file').slice(0, 3)}
                      </span>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-[#0f172a]">
                          {a.name || `Attachment ${idx + 1}`}
                        </span>
                        <span className="text-xs uppercase text-[#64748b]">{a.file_type || 'FILE'}</span>
                      </div>
                    </div>
                    <DownloadIcon className="size-5 text-[#64748b]" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-[#f8fafc] p-4 text-sm text-ink-secondary">
                No documents attached.
              </p>
            )}
          </div>

          {/* Comments */}
          {taskId && <CommentsPanel entityId={taskId} />}
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Assignee */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink">Assignee</h2>
            </div>
            <div className="mt-4 flex items-center gap-3">
              {assignee ? (
                <>
                  <span className="relative shrink-0">
                    <span className="flex size-12 items-center justify-center rounded-full border-2 border-white bg-brand-shade text-sm font-semibold text-brand shadow-card">
                      {initials(assignee)}
                    </span>
                    <span className="absolute bottom-0 right-0 size-3 rounded-full border-2 border-white bg-[#22c55e]" />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-ink">{assignee}</span>
                    <span className="text-xs font-semibold text-brand">Coordinator</span>
                  </div>
                </>
              ) : (
                <span className="text-sm italic text-ink-secondary">Unassigned</span>
              )}
            </div>
            {task && taskDesc(task) && (
              <p className="mt-4 border-t border-[#f3f4f6] pt-4 text-sm leading-[22px] text-ink-secondary">
                {taskDesc(task)}
              </p>
            )}
          </div>

          {/* Activity History */}
          {taskId && <ActivityHistoryPanel entityId={taskId} />}
        </div>
      </div>

      <ConfirmDeleteModal
        open={!!subToDelete}
        title="Delete sub task"
        message={
          subToDelete
            ? `Are you sure you want to delete "${taskTitle(subToDelete)}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete sub task"
        loading={isDeleting}
        onClose={() => setSubToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
